import { Box, Tab } from '@mui/material';
import { DragIndicator } from '@mui/icons-material';
import { useDroppable } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IconButton } from 'components/mui/common/IconButton';
import type { Props as TabsProps, TabId } from '../Tabs';
import { getDroppableTabId } from './utils/droppableTab';

type DroppableTabProps = {
  tab: TabsProps['tabs'][number];
  sectionId?: string;
  getCount: (count?: number) => { icon?: JSX.Element };
  sortable?: boolean;
  sectionName?: string;
  onTabClick?: (tab: TabId) => void;
};

const hoverBackground = 'rgba(0, 0, 0, 0.05)';

const PlainDroppableTab = ({ tab, getCount, sectionId, onTabClick }: DroppableTabProps) => {
  const droppableId = getDroppableTabId(tab.id, sectionId);
  const { setNodeRef, isOver } = useDroppable({
    id: droppableId,
    data: { tabId: tab.id },
  });
  const count = getCount(tab.count);

  return (
    <Tab
      ref={setNodeRef}
      onClick={() => onTabClick?.(tab.id)}
      sx={{ textTransform: 'initial', backgroundColor: isOver ? hoverBackground : undefined }}
      {...count}
      iconPosition="end"
      label={tab.label}
      value={tab.id}
      disabled={tab.disable}
      hidden={tab.hide}
    />
  );
};

const SortableDroppableTab = ({ tab, getCount, sectionId, sectionName, onTabClick }: DroppableTabProps) => {
  const droppableId = getDroppableTabId(tab.id, sectionId);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging, isOver, isSorting } = useSortable({
    id: droppableId,
    data: { type: sectionId ? 'subTab' : 'tab', tabNumber: tab.id, sectionId, sectionName },
  });
  const count = getCount(tab.count);

  return (
    <Tab
      ref={setNodeRef}
      onClick={() => onTabClick?.(tab.id)}
      sx={{
        textTransform: 'initial',
        transform: isSorting ? CSS.Translate.toString(transform) : undefined,
        transition: isSorting ? transition : undefined,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 50 : undefined,
        backgroundColor: isOver ? hoverBackground : undefined,
      }}
      {...count}
      iconPosition="end"
      label={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton component="span" size={24} {...attributes} {...listeners} sx={{ cursor: 'grab', touchAction: 'none', flexShrink: 0 }}>
            <DragIndicator fontSize="small" />
          </IconButton>
          {tab.label}
        </Box>
      }
      value={tab.id}
      disabled={tab.disable}
      hidden={tab.hide}
    />
  );
};

export const DroppableTab = ({ sortable, ...props }: DroppableTabProps) =>
  sortable ? <SortableDroppableTab {...props} /> : <PlainDroppableTab {...props} />;
